// Observasjonsarkivet: den varige, søkbare historikken analytikerne jobber i.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// EIERMÅL 2026-08-17: «mulighet for å hente opp historikk for analytikerne på en
// smart måte — herunder også jobbe aktivt i innhentingsplaner for å gi fra oss
// gode produkter.»
//
// I dag finnes ingen historikk. `Hub` holder situasjonsbildet i MINNET, og
// `snapshot()` SLETTER spor når de er foreldet (`hub.ts:90`). Et bilde en soldat
// sendte i går er borte fra situasjonsbildet, og det fantes ingen annen kopi enn
// selve filen i medialageret — uten tid, sted, retning eller hvem som sendte den.
// En analytiker kunne altså ikke svare på «hva har vi sett i dette området denne
// uka», som er selve grunnspørsmålet i etterretning.
//
// ⚠ ARKIVET ER IKKE SITUASJONSBILDET, OG SKAL IKKE BLI DET.
// Situasjonsbildet svarer «hva er der NÅ» og skal glemme. Arkivet svarer «hva har
// vi SETT» og skal aldri glemme. Blandes de, får man enten et kart som fylles opp
// av gamle observasjoner til det er uleselig, eller et arkiv som mister det
// operatøren trengte i etterkant. Derfor er dette en egen, append-only journal —
// ikke en lengre levetid på `Track`.
import { haversineM } from './geo.js';
export const ARKIV_MAKS_TREFF = 500;
function iBoks(o, b) {
    return o.lon >= b.minLon && o.lon <= b.maxLon && o.lat >= b.minLat && o.lat <= b.maxLat;
}
/** Alt søkbart tekstinnhold i én observasjon, i små bokstaver. */
function tekstFelt(o) {
    const deler = [o.label, o.kilde, o.rapportor ?? ''];
    for (const [k, v] of Object.entries(o.extra ?? {}))
        deler.push(k, v);
    return deler.join(' ').toLowerCase();
}
/**
 * Søk i arkivet.
 *
 * ⚠ DENNE FUNKSJONEN GJØR IKKE TILGANGSKONTROLL, OG DET ER MED VILJE.
 * Scope-filtrering hører i hubben, der den autentiserte brukeren finnes
 * (`scopeSnapshot`). Å legge en `bruker`-parameter her ville invitert til at
 * KLIENTEN kaller den — og en tilgangskontroll som kjører i nettleseren er ingen
 * tilgangskontroll (§4: håndheves autoritativt i backend). Kalleren skal filtrere
 * på org FØR eller ETTER dette kallet, og hubben gjør det.
 *
 * Nyeste først: en analytiker leser bakover fra nå.
 */
export function sokArkiv(obs, q = {}) {
    const tekst = q.tekst?.trim().toLowerCase();
    const ut = [];
    for (const o of obs) {
        if (matcherArkivSporring(o, q, tekst))
            ut.push(o);
    }
    ut.sort((a, b) => b.tsMs - a.tsMs);
    const maks = q.maks ?? ARKIV_MAKS_TREFF;
    return ut.slice(0, maks);
}
/**
 * Vilkåret ÉN observasjon vurderes mot — trukket ut av `sokArkiv` slik at
 * disk-strømmingen i `arkiv.ts` (SENS-27: søk skal dekke hele arkivet, ikke
 * bare `MAKS_I_MINNE`-vinduet) kan bruke NØYAKTIG samme filter linje for
 * linje, uten å duplisere det og risikere at de to søkene svarer forskjellig.
 *
 * `tekstLc` kan forhåndsberegnes av kalleren (én gang per søk) — strømmingen
 * kaller dette per linje og skal ikke gjøre `trim().toLowerCase()` på nytt
 * for hver av potensielt hundretusener av rader.
 */
export function matcherArkivSporring(o, q, tekstLc) {
    if (q.fraMs !== undefined && o.tsMs < q.fraMs)
        return false;
    if (q.tilMs !== undefined && o.tsMs > q.tilMs)
        return false;
    if (q.boks && !iBoks(o, q.boks))
        return false;
    if (q.naerPunkt) {
        const d = haversineM({ lat: q.naerPunkt.lat, lon: q.naerPunkt.lon }, { lat: o.lat, lon: o.lon });
        if (d > q.naerPunkt.radiusM)
            return false;
    }
    if (q.kinds?.length && !q.kinds.includes(o.kind))
        return false;
    if (q.kilder?.length && !q.kilder.includes(o.kilde))
        return false;
    if (q.rapportor && o.rapportor !== q.rapportor)
        return false;
    if (q.medBilde === true && !o.bildeUrl)
        return false;
    if (q.medBilde === false && o.bildeUrl)
        return false;
    if (q.rfiId && !(o.rfiIder ?? []).includes(q.rfiId))
        return false;
    const tekst = tekstLc ?? q.tekst?.trim().toLowerCase();
    if (tekst && !tekstFelt(o).includes(tekst))
        return false;
    return true;
}
/**
 * Spor → arkivpost.
 *
 * Returnerer `null` for spor som IKKE skal arkiveres. Det er en kort, bevisst
 * liste: PERIODISKE POSISJONSRAPPORTER hører ikke i observasjonsarkivet. En
 * telefon som melder hvert 5. minutt ville fylt journalen med titusener av
 * «Ola var her» — og begravd de faktiske observasjonene analytikeren leter etter.
 * Sporhistorikken (hvor folk har gått) er en annen sak med andre krav, og hører
 * i et bevegelses-/POL-lager, ikke her.
 */
export function sporTilObservasjon(t, mottattMs) {
    const kind = observasjonsTypeFor(t);
    if (!kind)
        return null;
    const o = {
        id: arkivId(t),
        tsMs: t.tsMs || mottattMs,
        mottattMs,
        kind,
        kilde: t.source,
        lat: t.lat,
        lon: t.lon,
        label: t.label,
    };
    if (t.cepM !== undefined)
        o.cepM = t.cepM;
    if (t.siktsektor) {
        o.asimutDeg = t.siktsektor.bearingDeg;
        o.sektorHalvbreddeDeg = t.siktsektor.bearingSigmaDeg;
        o.rekkeviddeM = t.siktsektor.rMaxM;
    }
    const bilde = t.extra?.['Bilde'];
    if (bilde)
        o.bildeUrl = bilde;
    if (t.person?.matrixId)
        o.rapportor = t.person.matrixId;
    if (t.org)
        o.org = t.org;
    if (t.extra)
        o.extra = { ...t.extra };
    return o;
}
/**
 * Unik arkiv-id for ett spor.
 *
 * ⚠ SPOR-ID ER IKKE ALLTID UNIK PER OBSERVASJON, og det tok jeg nesten feil.
 * Frigate-sporet har med vilje ÉN id per KAMERA (`frigate:port-no`) slik at
 * markøren oppdateres i stedet for å stables — se `frigate.ts`. Brukte arkivet
 * den id-en, ville hver ny deteksjon overskrevet den forrige, og et kamera med
 * hundre hendelser i døgnet ville etterlatt ÉN arkivpost. Da er det ikke et arkiv.
 *
 * Løsningen er kildens egen hendelses-id når den finnes (`Frigate-id` i `extra`).
 * Den er stabil gjennom `new`→`update` for SAMME hendelse, så gjentatte
 * oppdateringer dedupliserer riktig, men skiller to ulike hendelser.
 *
 * Feltbilder og geotagger har alt tidspunktet i spor-id-en og er unike i seg selv.
 */
export function arkivId(t) {
    const hendelse = t.extra?.['Frigate-id'];
    return hendelse ? `${t.id}#${hendelse}` : t.id;
}
/** Hvilken arkivtype hører sporet til? `null` = skal ikke arkiveres. */
export function observasjonsTypeFor(t) {
    if (t.id.startsWith('bilde:'))
        return 'bilde';
    if (t.id.startsWith('bildemaal:'))
        return 'maalestimat';
    if (t.id.startsWith('geotag:'))
        return 'geotag';
    if (t.id.startsWith('nod:'))
        return 'nod';
    if (t.source === 'frigate')
        return 'deteksjon';
    // ⚠ BFT/periodiske posisjoner arkiveres IKKE. Se `sporTilObservasjon`.
    return null;
}
/**
 * Observasjoner → `Ping[]` for mønster-i-liv-analyse (`patternoflife.ts`).
 *
 * Broen finnes fordi `patternOfLife()` er skrevet, testet og aldri brukt (0
 * kallsteder pr. 2026-08-17). Den grupperer gjentatte besøk i tid og rom og
 * finner topptimer — altså nøyaktig «har noe et mønster her?», som er det
 * arkivet gjør mulig i det øyeblikket det finnes historikk å spørre i.
 */
export function observasjonerTilPings(obs) {
    return obs.map((o) => ({ tsMs: o.tsMs, lat: o.lat, lon: o.lon }));
}
/**
 * Hvor har vi IKKE sett noe?
 *
 * ⚠ NAVNET HAR `arkiv`-PREFIKS FORDI `ugs.ts` ALT EKSPORTERER `dekningshull`
 * (dekningshull i utlegg av bakkesensorer — et annet spørsmål). Begge går ut
 * gjennom samme barrel (`@skade/contracts`), så to like navn ville latt den ene
 * skygge den andre i STILLHET. Funnet ved at hub-bygget plutselig typet mitt
 * arkiv-kall mot UGS-signaturen (2026-08-17).
 *
 *
 * Deler boksen i et grovt rutenett og teller observasjoner per celle i vinduet.
 * Celler med 0 er kandidater til innhenting — dette er broen fra arkivet til
 * innhentingsplanen, og svaret på «hvor skal vi sende noen».
 *
 * ⚠ «INGEN OBSERVASJON» BETYR IKKE «INGENTING DER». Det betyr at ingen har SETT
 * etter, eller at ingen rapporterte. Et tomt felt i dette rutenettet er en
 * KUNNSKAPSMANGEL, ikke en negativ observasjon — og den forskjellen er hele
 * grunnen til at innhentingsplaner finnes. Kalleren SKAL merke visningen slik;
 * en tom celle som ser ut som «avklart» er en aktiv løgn om hva vi vet.
 */
export function arkivDekningshull(obs, boks, opts) {
    const celle = opts.celleGrader && opts.celleGrader > 0 ? opts.celleGrader : 0.01;
    const iVindu = obs.filter((o) => o.tsMs >= opts.fraMs && o.tsMs <= opts.tilMs);
    const ut = [];
    // Vakt mot en absurd stor boks: 4000 celler er nok til et HV-distrikt, og
    // hindrer at et feiltastet søk bygger en liste på millioner.
    const maksCeller = 4000;
    for (let lat = boks.minLat; lat < boks.maxLat && ut.length < maksCeller; lat += celle) {
        for (let lon = boks.minLon; lon < boks.maxLon && ut.length < maksCeller; lon += celle) {
            const antall = iVindu.filter((o) => o.lat >= lat && o.lat < lat + celle && o.lon >= lon && o.lon < lon + celle).length;
            ut.push({ senter: { lat: lat + celle / 2, lon: lon + celle / 2 }, antall });
        }
    }
    return ut;
}
/** Kort, menneskelesbar oppsummering av et søkeresultat — til panel-topplinja. */
export function arkivSammendrag(treff) {
    if (!treff.length)
        return 'Ingen observasjoner i utvalget';
    const medBilde = treff.filter((o) => o.bildeUrl).length;
    const eldst = Math.min(...treff.map((o) => o.tsMs));
    const nyest = Math.max(...treff.map((o) => o.tsMs));
    const dager = Math.max(1, Math.round((nyest - eldst) / 86_400_000));
    const typer = new Set(treff.map((o) => o.kind));
    return `${treff.length} observasjoner · ${medBilde} med bilde · ${typer.size} type(r) · over ${dager} døgn`;
}
// ── OPPBEVARING (eierbeslutning B-009, 2026-08-18: «90 dager er fint») ──────
/**
 * Hvor lenge en arkivpost med PERSONOPPLYSNINGER kan ligge.
 *
 * ── ⚠ HVORFOR DET FINNES EN GRENSE HER OG IKKE ELLERS ───────────────────────
 * Arkivet er ellers uten oppbevaringsfrist, og det er riktig: egne observasjoner —
 * hva VÅRE sensorer så, hvor VÅRE folk var — er operativ historikk vi har både rett
 * og grunn til å beholde.
 *
 * Åpne kilder er noe annet. En politimelding handler om identifiserbare mennesker:
 * sted + tid + hendelse kan peke ut en person i et lite lokalsamfunn selv uten navn.
 * ⚠ NLOD 2.0 gir oss lov til å BRUKE politiets data; den avgjør ikke hvor lenge vi
 * kan lagre personopplysninger utledet av dem. Et permanent søkbart arkiv er en helt
 * annen behandling enn et situasjonsbilde som tømmes etter en uke.
 *
 * Tallet er eiers (B-009). Det står HER, ett sted, slik at det kan rettes uten å
 * lete — og slik at ingen kan si at systemet lagret uten en frist.
 */
export const OPPBEVARING_PERSONOPPLYSNINGER_MS = 90 * 24 * 60 * 60_000;
/** Merket adapteren setter når posten inneholder personopplysninger. */
export const PERSONOPPLYSNINGER_NOKKEL = 'personopplysninger';
export function harPersonopplysninger(o) {
    return o.extra?.[PERSONOPPLYSNINGER_NOKKEL] === 'ja';
}
/**
 * Skal posten slettes?
 *
 * ── ⚠ UNNTAKET FOR RFI-KOBLEDE POSTER, OG RISIKOEN I DET ────────────────────
 * En post som besvarer et etterretningskrav har en operativ grunn til å bli
 * bevart, og det var en del av forslaget eier sa ja til. Men unntaket er samtidig
 * et SMUTTHULL: knytter noen alt til en RFI, lagrer vi alt for alltid, og fristen er
 * borte i praksis.
 *
 * Derfor er unntaket gjort SYNLIG framfor stille: `oppbevaringstall()` teller hvor
 * mange poster som holdes tilbake på dette grunnlaget, og tallet hører i
 * hubbens statuslinje. Vokser det, er det noe eier skal få se — ikke noe som
 * forsvinner inn i en betingelse ingen leser.
 */
export function skalSlettes(o, naaMs) {
    if (!harPersonopplysninger(o))
        return false;
    // Unntak: posten besvarer et etterretningskrav.
    if (o.rfiIder && o.rfiIder.length > 0)
        return false;
    return naaMs - o.tsMs >= OPPBEVARING_PERSONOPPLYSNINGER_MS;
}
/**
 * Én linje om postens oppbevaring — til arkivpanelet.
 *
 * ⚠ SIER ALLTID NOE FOR POSTER MED PERSONOPPLYSNINGER. En analytiker som ser en
 * politimelding i arkivet skal kunne se at den har en frist, og hvor lenge det er
 * igjen — ellers oppdager hun først at den er borte.
 */
export function oppbevaringsForklaring(o, naaMs) {
    if (!harPersonopplysninger(o))
        return '';
    if (o.rfiIder && o.rfiIder.length > 0) {
        return `⚠ Bevares utover fristen fordi den besvarer ${o.rfiIder.length} RFI (${o.rfiIder.join(', ')})`;
    }
    const igjen = OPPBEVARING_PERSONOPPLYSNINGER_MS - (naaMs - o.tsMs);
    if (igjen <= 0)
        return '⚠ Over oppbevaringsfristen — slettes ved neste opprydding';
    const d = Math.floor(igjen / 86_400_000);
    return `Personopplysninger — slettes om ${d} døgn`;
}
/**
 * Tell oppbevaringstilstanden. Ren funksjon — lageret gjør slettingen.
 */
export function oppbevaringstall(poster, naaMs) {
    let medPersonopplysninger = 0;
    let tilSletting = 0;
    let bevartAvRfi = 0;
    for (const o of poster) {
        if (!harPersonopplysninger(o))
            continue;
        medPersonopplysninger++;
        const overFristen = naaMs - o.tsMs >= OPPBEVARING_PERSONOPPLYSNINGER_MS;
        if (!overFristen)
            continue;
        if (o.rfiIder && o.rfiIder.length > 0)
            bevartAvRfi++;
        else
            tilSletting++;
    }
    return { medPersonopplysninger, tilSletting, bevartAvRfi };
}
//# sourceMappingURL=observasjonsarkiv.js.map